/**
 * Visual-pane starter (S8) — shown when the Visual source tab is active over
 * an EMPTY buffer. The compact VQE surface edits an existing statement; this
 * bootstraps one: pick a table → seed `SELECT * FROM <table>` (visual
 * provenance), after which the compact sections take over. Mirrors the full
 * VQE's `VqeStarterCanvas` (same copy, same `starterTableGroups`/
 * `buildStarterSelect` helpers) but pulls its schema snapshot directly — the
 * dialog's resolver context only exists once a statement does.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { getEngineForConnection } from '../../../../connections/utils/connectionLookup';
import type { SchemaSnapshot } from '../../../../sql-resolver/types/schema';
import { CommandPickerPopover } from '../../../visual-query-editor/render/common/CommandPickerPopover';
import {
  buildLivePullSchemaSnapshot,
  databaseEngineIdToSqlEngine,
} from '../../../visual-query-editor/edit/livePullSchemaSnapshot';
import {
  buildStarterSelect,
  starterTableGroups,
} from '../../../visual-query-editor/workspace/starterTableGroups';
import { vqeCopy } from '../../../visual-query-editor/ux/vqeCopy';

export function VisualStarter({
  connectionId,
  database,
  onSeedSql,
  onShowSql,
}: {
  connectionId: string;
  database?: string;
  /** Seed the dialog buffer with a starter SELECT (visual provenance). */
  onSeedSql: (sql: string) => void;
  onShowSql: () => void;
}) {
  const [snapshot, setSnapshot] = useState<SchemaSnapshot | null>(null);
  const [loaded, setLoaded] = useState(false);

  const engine = useMemo(
    () => (connectionId ? databaseEngineIdToSqlEngine(getEngineForConnection(connectionId)) : null),
    [connectionId],
  );

  useEffect(() => {
    setSnapshot(null);
    setLoaded(false);
    if (!connectionId || !engine) return;
    let cancelled = false;
    buildLivePullSchemaSnapshot(connectionId, engine)(database)
      .then((snap) => {
        if (cancelled) return;
        setSnapshot(snap ?? null);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [connectionId, engine, database]);

  const groups = useMemo(() => starterTableGroups(snapshot), [snapshot]);
  const tableByKey = useMemo(
    () => new Map(groups.flatMap((g) => g.items.map((item) => [item.key, item.table] as const))),
    [groups],
  );

  // Empty-schema honesty renders ONCE: the inline hint below IS the message
  // (the database picker in the dialog chrome makes it actionable). The
  // picker trigger disables with the same copy as its WHY-tooltip — an
  // enabled picker would only reopen the identical sentence as the popover's
  // inline (portalled={false}) empty state right under the buttons, which is
  // exactly the user-reported doubled note.
  const emptySchema = loaded && (snapshot?.tables.size ?? 0) === 0;
  const disabled = !connectionId || !loaded || emptySchema;
  const tooltip = !connectionId
    ? vqeCopy.disabled.noConnection
    : !loaded
      ? vqeCopy.disabled.noSnapshot
      : emptySchema
        ? vqeCopy.canvas.noTables
        : undefined;

  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-6 text-center text-xs text-muted-foreground"
      data-testid="add-tile-vqe-starter"
    >
      <p className="font-medium text-foreground">{vqeCopy.canvas.title}</p>
      <p>{vqeCopy.canvas.body}</p>
      {emptySchema ? (
        <p className="italic" data-testid="add-tile-vqe-no-tables">
          {vqeCopy.canvas.noTables}
        </p>
      ) : null}
      <div className="mt-2 flex items-center justify-center gap-2">
        {/* Action hierarchy: picking a table is THE path this pane exists for,
            so it carries the filled button; "Start from SQL" is the escape
            hatch and steps down to a quiet link. The shared picker hardcodes
            a ghost trigger, so the emphasis is applied from this wrapper
            (descendant selector — higher specificity than the Button's own
            utilities) rather than forking CommandPickerPopover. */}
        <div className="[&>span>button:not(:disabled)]:bg-primary [&>span>button:not(:disabled)]:text-primary-foreground [&>span>button:not(:disabled)]:shadow-sm [&>span>button:not(:disabled)]:hover:bg-primary/90">
          <CommandPickerPopover
            testId="add-tile-vqe-starter-picker"
            triggerLabel={vqeCopy.canvas.startFromTable}
            groups={groups}
            disabled={disabled}
            tooltip={tooltip}
            onSelect={(key) => {
              const table = tableByKey.get(key);
              if (table && engine) onSeedSql(buildStarterSelect(table, engine));
            }}
            searchPlaceholder={vqeCopy.canvas.searchPlaceholder}
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground underline-offset-4 hover:underline"
          onClick={onShowSql}
          data-testid="add-tile-vqe-start-from-sql"
        >
          {vqeCopy.canvas.startFromSql}
        </Button>
      </div>
    </div>
  );
}
